import { createTheme, alpha } from '@mui/material/styles';

/** Linear / Modern dark theme — aligned with Employee Portal tokens */
const ACCENT = '#5E6AD2';
const ACCENT_BRIGHT = '#6872D9';
const BG_BASE = '#050506';
const BG_ELEVATED = '#0a0a0c';
const FG = '#EDEDEF';
const FG_MUTED = '#8A8F98';
const BORDER = 'rgba(255,255,255,0.06)';

export const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'dark',
    primary: { main: ACCENT, light: ACCENT_BRIGHT },
    secondary: { main: '#7C4DFF' },
    success: { main: '#1B8F5A' },
    warning: { main: '#B85C00' },
    error: { main: '#B3261E' },
    background: {
      default: BG_BASE,
      paper: BG_ELEVATED,
    },
    text: {
      primary: FG,
      secondary: FG_MUTED,
    },
    divider: BORDER,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"Inter","Geist Sans",system-ui,-apple-system,sans-serif',
    h5: { fontWeight: 600, letterSpacing: -0.3 },
    h6: { fontWeight: 600, letterSpacing: -0.2 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: BG_BASE,
          backgroundImage:
            'radial-gradient(ellipse at top, #0a0a0f 0%, #050506 50%, #020203 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
        '::selection': {
          backgroundColor: alpha(ACCENT, 0.35),
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha('#fff', 0.03),
          border: `1px solid ${BORDER}`,
          boxShadow:
            '0 0 0 1px rgba(255,255,255,0.04), 0 2px 20px rgba(0,0,0,0.4), 0 0 40px rgba(0,0,0,0.2)',
        },
        outlined: {
          borderColor: BORDER,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(BG_ELEVATED, 0.85),
          backdropFilter: 'blur(16px) saturate(140%)',
          borderBottom: `1px solid ${BORDER}`,
          color: FG,
        },
      },
    },
    MuiButton: {
      defaultProps: { variant: 'contained' },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: 16,
          paddingBlock: 10,
          transition: 'background 200ms cubic-bezier(0.16,1,0.3,1), transform 200ms cubic-bezier(0.16,1,0.3,1)',
          '&:active': { transform: 'scale(0.98)' },
        },
        containedPrimary: {
          backgroundColor: ACCENT,
          boxShadow: `0 0 0 1px ${alpha(ACCENT, 0.5)}, 0 4px 12px ${alpha(ACCENT, 0.3)}, inset 0 1px 0 0 rgba(255,255,255,0.2)`,
          '&:hover': {
            backgroundColor: ACCENT_BRIGHT,
          },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.12)',
          color: FG,
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.2)',
            backgroundColor: alpha('#fff', 0.05),
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { fullWidth: true, size: 'medium' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#0F0F12',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.16)' },
            '&.Mui-focused fieldset': {
              borderColor: ACCENT,
              boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.3)}`,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          backgroundColor: BG_ELEVATED,
          border: `1px solid ${BORDER}`,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          borderRadius: 2,
          backgroundColor: ACCENT,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: FG_MUTED,
          '&.Mui-selected': { color: FG },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: BORDER },
      },
    },
  },
});
